import React, { ReactElement, ReactNode } from "react";
import {
    RiAttachment2, RiBracesLine, RiBracketsLine, RiCalendarLine, RiCalendarScheduleLine,
    RiCheckboxLine, RiDropdownList, RiFlashlightLine, RiGroupLine, RiInputField,
    RiLink, RiListCheck2, RiListCheck3, RiListRadio, RiLockPasswordLine, RiMailLine,
    RiMindMap,
    RiPassValidLine, RiRadioButtonLine, RiRadioLine, RiSearchLine, RiTableView, RiTextBlock,
    RiTimeLine, RiTimelineView, RiUserLine,
} from "@remixicon/react";
import { FormFieldType } from "../../../__generated__/api";
import Select, { Option } from "../../select/Select";
import { Filters } from "../../filters/Filters";
import { ReactComponent as NumbersIcon } from "../../../assets/icons/numbers.svg";

const CHOICE_LABELS = ["Group", "Options"];

export enum UserType {
    END_USER = "END_USER",
    ADMINISTRATOR = "ADMINISTRATOR",
    JSON = "JSON"
}

export const CUSTOM_FIELD_PROPS = {

    [FormFieldType.Text]: {
        text: "Text",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
        icon: <RiInputField />,
    },

    [FormFieldType.Password]: {
        text: "Password",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
        icon: <RiPassValidLine />,
    },

    [FormFieldType.LongText]: {
        text: "Long Text",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
        icon: <RiTextBlock />,
    },

    [FormFieldType.Number]: {
        text: "Number",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
        icon: <NumbersIcon />,
    },
    [FormFieldType.Decimal]: {
        text: "Decimal",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
        icon: <NumbersIcon />,
    },

    [FormFieldType.Boolean]: {
        text: "Yes/No",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
        icon: <RiCheckboxLine />,
    },

    [FormFieldType.Date]: {
        text: "Date",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
        icon: <RiCalendarLine />,
    },

    [FormFieldType.Time]: {
        text: "Time",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
        icon: <RiTimeLine />,
    },

    [FormFieldType.DateTime]: {
        text: "Date and Time",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
        icon: <RiCalendarScheduleLine />,
    },

    [FormFieldType.AsyncDropdown]: {
        text: "Choice",
        canCreate: false,
        canEnterChoice: false,
        childLevels: 1,
        labels: CHOICE_LABELS,
        permissions: UserType.END_USER,
        icon: <RiDropdownList />,
    },
    [FormFieldType.MultiLevelDropdown]: {
        text: "Multi-level Choice",
        canCreate: true,
        canEnterChoice: true,
        childLevels: -1,
        labels: undefined,
        permissions: UserType.END_USER,
        icon: <RiTimelineView />,
    },
    [FormFieldType.MultiSelectDropdown]: {
        text: "Multiple Choice",
        canCreate: true,
        canEnterChoice: true,
        childLevels: 2,
        labels: CHOICE_LABELS,
        permissions: UserType.END_USER,
        icon: <RiListCheck3 />,
    },
    [FormFieldType.Dropdown]: {
        text: "Choice",
        canCreate: true,
        canEnterChoice: true,
        childLevels: 2,
        labels: CHOICE_LABELS,
        permissions: UserType.END_USER,
        icon: <RiDropdownList />,
    },
    [FormFieldType.Checkboxes]: {
        text: "Checkboxes",
        canCreate: true,
        canEnterChoice: true,
        childLevels: 1,
        labels: [],
        permissions: UserType.END_USER,
        icon: <RiListCheck2 />,
    },

    [FormFieldType.SearchText]: {
        text: "Search Text",
        canCreate: false,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
        icon: <RiSearchLine />,
    },

    [FormFieldType.User]: {
        text: "User",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
        icon: <RiUserLine />,
    },
    [FormFieldType.Users]: {
        text: "Users",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
        icon: <RiGroupLine />,
    },
    [FormFieldType.Json]: {
        text: "Json",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.ADMINISTRATOR,
        icon: <RiBracesLine />,
    },
    [FormFieldType.Workflow]: {
        text: "Workflow",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.ADMINISTRATOR,
        icon: <RiFlashlightLine />,
    },
    [FormFieldType.WorkflowTicket]: {
        text: "Workflow - Ticket",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.ADMINISTRATOR,
        icon: <RiFlashlightLine />,
    },
    [FormFieldType.Files]: {
        text: "Files",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
        icon: <RiAttachment2 />,
    },
    [FormFieldType.Email]: {
        text: "Email Address",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
        icon: <RiMailLine />,
    },
    [FormFieldType.Url]: {
        text: "Link",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
        icon: <RiLink />,
    },
    record: {
        text: "record",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.JSON,
        icon: <RiTableView />,
    },
    array: {
        text: "array",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.JSON,
        icon: <RiBracketsLine />,
    },
    map: {
        text: "map",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.JSON,
        icon: <RiMindMap />,
    },
};

const ALL_FIELDS = Object.entries(CUSTOM_FIELD_PROPS).filter(([, row]) => row.canCreate);

const END_USER_FIELDS = ALL_FIELDS
    .filter(([, row]) => row.permissions === UserType.END_USER)
    .map(([value, row]) => ({
        label: row.text,
        value,
    }));

const ADMIN_FIELDS = ALL_FIELDS
    .filter(([, row]) => row.permissions === UserType.ADMINISTRATOR || row.permissions === UserType.END_USER)
    .map(([value, row]) => ({
        label: row.text,
        value,
    }));

const JSON_FIELDS = ALL_FIELDS
    .filter(([, row]) => row.permissions === UserType.ADMINISTRATOR
        || row.permissions === UserType.END_USER
        || row.permissions === UserType.JSON)
    .map(([value, row]) => ({
        label: row.text,
        value,
    }));

export function getFieldsForUserType(type: UserType | undefined) {
    if (type === UserType.ADMINISTRATOR) {
        return ADMIN_FIELDS;
    }
    if (type === UserType.JSON) {
        return JSON_FIELDS;
    }
    return END_USER_FIELDS;
}

export default function FieldType<TIsMulti extends boolean = false, TIsClearable=false>({
    onChange, value, className, allowedTypes, type,
    shouldShowDropdown, toggleText, toggleClassName, icon,
    isMulti, isClearable,
}: {
    onChange: (
        sourceId: (TIsMulti extends true ? FormFieldType[] : FormFieldType) | (TIsClearable extends true ? undefined : never),
    ) => void;

    value: (TIsMulti extends true ? FormFieldType[] : FormFieldType) | undefined;

    className?: string;
    allowedTypes?: FormFieldType[];
    type?: UserType;

    isMulti?: TIsMulti;
    isClearable?: TIsClearable;

    shouldShowDropdown?: boolean;
    toggleClassName?: string;
    toggleText?: string
    icon?: ReactNode;

}): ReactElement {
    let fields = getFieldsForUserType(type);
    if (allowedTypes) {
        const types = new Set(allowedTypes);
        fields = fields.filter(row => types.has(row.value as FormFieldType));
    }

    if (shouldShowDropdown) {
        return (
            <Filters
                toggleText={toggleText}
                isMulti={isMulti}
                icon={icon}
                categories={fields}
                className={className}
                toggleClassName={toggleClassName}
                selectedCategories={
                        (isMulti ? new Set(value) : (value as string)) as TIsMulti extends true ? Set<string> : string
                }
                onSelected={current => (isMulti
                    ? (onChange as ((value: string[]) => void))(Array.from(current))
                    : (onChange as ((value: string) => void))(current as string))}
            />
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<FormFieldType>[] : Option<FormFieldType>;
    type ValueType = TIsMulti extends true ? Option<FormFieldType>[] : Option<FormFieldType>;

    const handleSelected = (selected: SelectedType) => {
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as FormFieldType)
            : (selected as Option<string>)?.value) as Parameters<typeof onChange>[0];
        onChange(values);
    };

    const selected = Array.isArray(value)
        ? value.map((row: FormFieldType) => ({
            value: row,
            label: CUSTOM_FIELD_PROPS[row]?.text,
        })).filterNull()
        : {
            value,
            label: CUSTOM_FIELD_PROPS[value as FormFieldType]?.text,
        };

    return (
        <Select
            className={className}
            placeholder=""
            onChange={handleSelected}
            options={fields}
            isClearable={isClearable as boolean}
            value={selected as ValueType}
        />
    );
}

FieldType.defaultProps = {
    className: "",
    allowedTypes: undefined,
    type: UserType.END_USER,

    shouldShowDropdown: undefined,
    toggleClassName: undefined,
    toggleText: undefined,
    icon: undefined,
    isClearable: false,
    isMulti: false,
};
